import pako from 'pako';

/**
 * Converts a Uint8Array to URL-safe Base64 string
 */
const toBase64UrlSafe = (buffer) => {
  const binary = Array.from(buffer)
    .map(byte => String.fromCharCode(byte))
    .join('');

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

/**
 * Converts URL-safe Base64 string back to Uint8Array
 */
const fromBase64UrlSafe = (str) => {
  // Restore standard Base64 format
  str = str.replace(/-/g, '+').replace(/_/g, '/');

  // Add padding if needed
  while (str.length % 4) {
    str += '=';
  }

  const binary = atob(str);
  return Uint8Array.from(binary, c => c.charCodeAt(0));
};

/**
 * Compresses data for URL storage
 * Version 2: Uses pako deflate compression
 *
 * @param {any} data - Data to compress (will be JSON stringified)
 * @returns {string} - Compressed string with v2: prefix
 */
export const compressForUrl = (data) => {
  try {
    const json = JSON.stringify(data);

    // Always compress to ensure consistent format
    const deflated = pako.deflate(json);
    const base64 = toBase64UrlSafe(deflated);
    return `v2:${base64}`;
  } catch (err) {
    console.error('Compression failed, fallback to uncompressed', err);
    return JSON.stringify(data);
  }
};

/**
 * Decompresses data from URL storage
 * Supports both v2: (compressed) and v1 (uncompressed) formats
 *
 * @param {string} param - URL parameter value
 * @returns {any} - Decompressed and parsed data
 */
export const decompressFromUrl = (param) => {
  if (!param) {
    return {};
  }

  try {
    // Version 2: Compressed with pako
    if (param.startsWith('v2:')) {
      const compressed = param.substring(3);
      const decoded = fromBase64UrlSafe(compressed);
      const inflated = pako.inflate(decoded, { to: 'string' });
      return JSON.parse(inflated);
    }

    // Version 1: Uncompressed (legacy support)
    return JSON.parse(param);
  } catch (err) {
    console.error('Decompression failed', err);
    return {};
  }
};

/**
 * Checks if a URL parameter is compressed
 *
 * @param {string} param - URL parameter value
 * @returns {boolean} - True if compressed (v2:)
 */
export const isCompressed = (param) => param?.startsWith('v2:');
